import React, {useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography,} from "@mui/material";
import Busy from "../Busy";
import {requestDeactivateAccountPaymentMethod} from "./request/payment-method-requests";
import {styled} from "@mui/material/styles";
import {Account} from "../../types/Account";
import {PaymentMethod} from "../../types/PaymentMethod";

// Cast restores Typography's polymorphic `component` prop typing, which styled() drops
const MarginTop10 = styled(Typography)(() => ({
  marginTop: '0.71rem',
})) as typeof Typography;

const BackgroundGrey100 = styled(DialogActions)(({theme}) => ({
  backgroundColor: theme.palette.grey[100],
}));

/**
 * Error shape returned when a payment method cannot be removed. `errors` is keyed by the
 * order (booking) numbers still using the payment method.
 */
interface DeactivatePaymentMethodError {
  response?: {
    body?: {
      message?: string;
      errors?: Record<string, unknown>;
    };
  };
}

interface RemovePaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (response: unknown) => void;
  account: Pick<Account, 'id'>;
  paymentMethod?: Pick<PaymentMethod, 'id'> | null;
}

const RemovePaymentMethodDialog = ({
                                     open,
                                     onClose,
                                     onSuccess,
                                     account,
                                     paymentMethod,
                                   }: RemovePaymentMethodDialogProps) => {
  const [errorMsg, setErrorMsg] = useState<React.ReactNode>('');

  const resetDialog = (): void => {
    setErrorMsg('');
    // Add other reset logic here
  };

  const handleClose = (): void => {
    resetDialog();
    onClose();
  };

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    // The dialog is only opened with a payment method selected; guard to satisfy strict null checks
    if (!paymentMethod) return;
    const handleSuccess = (response: unknown): void => {
      Busy.set(false);
      resetDialog();
      onSuccess(response);
    };
    const handleError = (rawError: unknown): void => {
      Busy.set(false);
      try {
        const error = rawError as DeactivatePaymentMethodError;
        if (error.response?.body?.message && error.response?.body?.errors) {
          const errorMessage = error.response.body;
          const orderNumbers = (): string => {
            let orderNumbers = '';
            for (const error in errorMessage?.errors) {
              orderNumbers += ' ' + error + ' ';
            }
            return orderNumbers;
          }

          const message = errorMessage?.message || "Error occurred:";
          let numbers = orderNumbers()?.trim() || "";

          numbers = numbers.replace(/\s*,\s*|\s+/g, ", ").trim();
          const orderNumbersArray = numbers.split(", ").filter(orderNumber => orderNumber);

          setErrorMsg(
            <span>
              {message}{" "}
              {orderNumbersArray.map((orderNumber, index, arr) => (
                <React.Fragment key={orderNumber}>
                  <a
                    href={`/bookings?bookingNumber=${encodeURIComponent(orderNumber.trim())}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {orderNumber.trim()}
                  </a>
                  {index < arr.length - 1 ? ", " : ""}
                </React.Fragment>
              ))}
            </span>
          );
        } else {
          setErrorMsg('An error occurred while removing the payment method.');
        }
      } catch {
        setErrorMsg('An error occurred while removing the payment method.');
      }
    };

    Busy.set(true);
    requestDeactivateAccountPaymentMethod(account.id, paymentMethod.id, handleSuccess, handleError);
  };

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby={'ss-remove-payment-method'}>
      <DialogTitle id={'ss-remove-payment-method'}>
        Remove Payment Method
      </DialogTitle>
      <DialogContent>
        <DialogContentText component={'strong'}>
          Are you sure you would like to remove this payment method? Please make sure to replace or update your
          payment method on file in order to process any payments from SecurSpace going forward.
        </DialogContentText>
        {errorMsg && (
          <MarginTop10 variant="body1" component="div" color="error" align="center">
            {errorMsg}
          </MarginTop10>
        )}
      </DialogContent>
      <BackgroundGrey100>
        <Button color={"primary"} onClick={handleClose}>Cancel</Button>
        <Button color={"primary"} onClick={handleSubmit}>Confirm</Button>
      </BackgroundGrey100>
    </Dialog>
  );
};

export default RemovePaymentMethodDialog;
